// Base class for all prop assets
// Each prop can be replaced with SVG/image later

export interface PropRenderContext {
  ctx: CanvasRenderingContext2D;
  x: number;      // Center X position
  y: number;      // Bottom Y position (ground level)
  scale: number;  // Zoom scale
  variant?: number; // Visual variant (0, 1, 2, etc.)
}

export abstract class PropAsset {
  abstract readonly id: string;
  abstract readonly width: number;   // Base width in pixels
  abstract readonly height: number;  // Base height in pixels

  // Collision properties
  abstract readonly walkable: boolean;      // Can player walk through this?
  abstract readonly collisionRadius: number; // Collision radius in tiles (0 = no collision)

  // Occlusion properties - does this prop hide the player when they're behind it?
  readonly occludesPlayer: boolean = false;

  // Cache for rendered sprites at different scales
  protected cache: Map<string, HTMLCanvasElement> = new Map();

  // Override this to draw the prop
  abstract draw(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, variant?: number): void;

  // Get cached canvas or create new one
  getCached(scale: number, variant: number = 0): HTMLCanvasElement {
    const key = `${scale.toFixed(1)}_${variant}`;

    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(this.width * scale) + 4;
    canvas.height = Math.ceil(this.height * scale) + 4;
    const ctx = canvas.getContext('2d')!;

    // Draw centered at bottom
    const cx = canvas.width / 2;
    const cy = canvas.height;

    this.draw(ctx, cx, cy, scale, variant);

    this.cache.set(key, canvas);
    return canvas;
  }

  // Render to screen (uses cache)
  render(context: PropRenderContext): void {
    const cached = this.getCached(context.scale, context.variant);
    context.ctx.drawImage(
      cached,
      context.x - cached.width / 2,
      context.y - cached.height
    );
  }

  // Clear cache (call when scale changes significantly)
  clearCache(): void {
    this.cache.clear();
  }
}
